import { throttle } from 'lodash-es'

import { parseLyrics, parsePlainLyrics } from '../MusicDetail/helpers'


import { useState, useRef, useCallback, useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
export interface LyricItem {
  time: number
  text: string
}
interface IProps {
  lyricsLrc?: string | null
  lyrics?: string | null // 纯文本歌词，作为降级展示
  containerClassName?: string
  lyricClassName?: string
  updateInterval?: number
  currentTime?: number
}
export default function ScrollLyric(props: IProps) {
  const {
    lyricsLrc,
    lyrics: plainLyrics,
    containerClassName,
    lyricClassName,
    updateInterval = 500,
    currentTime,
  } = props
  const [currentLineIndex, setCurrentLineIndex] = useState(-1)
  const lyricsRef = useRef<HTMLDivElement[]>([])

  // 优先使用 lyricsLrc（带时间戳），否则降级使用 plainLyrics（纯文本）
  const hasLrc = !!lyricsLrc?.trim()
  const lyrics = hasLrc ? parseLyrics(lyricsLrc) : parsePlainLyrics(plainLyrics)

  const containerRef = useRef<HTMLDivElement>(null)

  // 添加手动滚动检测相关状态
  const isAutoScrollEnabledRef = useRef<boolean>(true)
  const manualScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isAutoScrolling = useRef<boolean>(false)

  const updateLyrics = useCallback(
    throttle((currentTime: number) => {
      // 纯文本歌词没有时间戳，不执行基于时间的滚动
      if (!hasLrc) return

      // 找到当前时间对应的歌词行
      let newIndex = -1
      for (let i = 0; i < lyrics.length; i++) {
        if (currentTime >= lyrics[i].time) {
          newIndex = i
        } else {
          break
        }
      }
      setCurrentLineIndex(newIndex)
      // 如果自动滚动被禁用，则不执行滚动
      if (!isAutoScrollEnabledRef.current) return
      // 自动滚动到当前歌词行
      if (newIndex >= 0 && lyricsRef.current[newIndex]) {
        const container = containerRef.current
        const targetElement = lyricsRef.current[newIndex]
        if (container === null || targetElement === null) return
        // 计算目标元素相对于容器的偏移量
        const offsetTop = targetElement.offsetTop // 目标元素相对于容器顶部的偏移
        const containerHeight = container.clientHeight
        // 计算目标滚动位置，使歌词行居中
        const scrollTop =
          offsetTop - containerHeight / 2 + targetElement.clientHeight / 2
        isAutoScrolling.current = true
        // 使用 scrollTo 实现平滑滚动
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        })
        setTimeout(() => {
          isAutoScrolling.current = false
        }, 300)
      }
    }, updateInterval),
    [hasLrc, lyrics],
  )

  useEffect(() => {
    if (currentTime !== undefined) {
      updateLyrics(currentTime)
    }
  }, [currentTime])

  useEffect(() => {
    // 处理手动滚动
    const handleScroll = throttle(() => {
      if (!containerRef.current || isAutoScrolling.current) return
      isAutoScrollEnabledRef.current = false
      if (manualScrollTimeoutRef.current) {
        clearTimeout(manualScrollTimeoutRef.current)
      }
      manualScrollTimeoutRef.current = setTimeout(() => {
        isAutoScrollEnabledRef.current = true
      }, 5000)
    }, 50)

    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      // 清理定时器
      if (manualScrollTimeoutRef.current) {
        clearTimeout(manualScrollTimeoutRef.current)
      }
    }
  }, [])

  // 两种歌词都没有时不展示
  if (!lyricsLrc?.trim() && !plainLyrics?.trim()) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={{ position: 'relative', isolation: 'isolate' }}
    >
      {lyrics.map((line, index) => (
        <div
          key={index}
          ref={(el) => void (el && (lyricsRef.current[index] = el))}
          className={cls`${
            hasLrc && index === currentLineIndex
              ? 'text-f-text font-bold'
              : 'text-f-text-tertiary'
          } ${lyricClassName}`}
        >
          {line.text}
        </div>
      ))}
    </div>
  )
}
